import { Alert, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import moment from 'moment';
import { CommonVisitLogValidation, MonitoringVisitLogType } from 'src/types/MonitoringVisitLogType';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import './MonitoringLogs.scss';

interface Props {
  visitLog: MonitoringVisitLogType;
  setVisitLog: (visitLog: MonitoringVisitLogType) => void;
  visitTypesList: any;
  subVisitTypesList: any;
  validationObj: CommonVisitLogValidation;
  setValidationObj: (validationObj: CommonVisitLogValidation) => void;
  isEventEdited: boolean;
  setIsEventEdited: (value: boolean) => void;
  isActualDatesEdited: boolean;
  setIsActualDatesEdited: (value: boolean) => void;
}

export default function LogEditForm({
  visitLog,
  setVisitLog,
  visitTypesList,
  subVisitTypesList,
  validationObj,
  setValidationObj,
  isEventEdited,
  setIsEventEdited,
  isActualDatesEdited,
  setIsActualDatesEdited
}: Props) {
  const { userPreferredDateTimeFormat } = usePreferedDateFormat();

  const modifyScheduledDates = (newDateValue: any, propName: string) => {
    setIsEventEdited(true);
    modifyDates(newDateValue, propName);
  };
  const modifyActualDates = (newDateValue: any, propName: string) => {
    setIsActualDatesEdited(true);
    modifyDates(newDateValue, propName);
  };
  const modifyDates = (newDateValue: any, propName: string) => {
    if (newDateValue !== null) {
      setValidationObj({ ...validationObj, [propName]: false });
      setVisitLog({ ...visitLog, [propName]: moment.utc(newDateValue.$d).format() });
    } else {
      setVisitLog({ ...visitLog, [propName]: '' });
    }
  };
  return (
    <Grid container spacing={2} className="event-form">
      <Grid item xs={6}>
        <FormControl variant="standard" fullWidth disabled>
          <InputLabel id="visit-type">
            Visit Type <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            id="visit-type-select-standard"
            value={visitLog.visit_type}
            sx={{ minWidth: '265px' }}
            onChange={(e: any) => {
              setVisitLog({ ...visitLog, visit_type: e.target.value });
            }}
          >
            {visitTypesList &&
              visitTypesList.map((visit: any) => {
                return (
                  <MenuItem key={visit.id} value={visit.code_list_value}>
                    {visit.code_list_value}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl variant="standard" fullWidth error={validationObj.sub_type} disabled={isActualDatesEdited}>
          <InputLabel id="sub-visit-type">
            Sub Type <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Select
            id="sub-visit-type-select-standard"
            value={visitLog.sub_type}
            sx={{ minWidth: '265px' }}
            onChange={(e: any) => {
              setIsEventEdited(true);
              setValidationObj({ ...validationObj, sub_type: false });
              setVisitLog({ ...visitLog, sub_type: e.target.value });
            }}
          >
            {subVisitTypesList &&
              subVisitTypesList.map((subVisit: any) => {
                return (
                  <MenuItem key={subVisit.id} value={subVisit.code_list_value}>
                    {subVisit.code_list_value}
                  </MenuItem>
                );
              })}
          </Select>
          {validationObj.sub_type && <FormHelperText>{'Required!'}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl fullWidth margin="normal">
            <DateTimePicker
              disabled={isActualDatesEdited}
              className="modifylabel"
              label="Scheduled Start Date & Time"
              value={visitLog.start_date !== '' ? visitLog.start_date : null}
              inputFormat={userPreferredDateTimeFormat}
              maxDateTime={dayjs(visitLog.end_date)}
              closeOnSelect
              onChange={(newValue: any) => {
                modifyScheduledDates(newValue, 'start_date');
              }}
              onAccept={(newValue: any) => {
                modifyScheduledDates(newValue, 'start_date');
              }}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  error={validationObj.start_date || visitLog.start_date === 'Invalid date'}
                  helperText={validationObj.start_date && 'Required!'}
                />
              )}
            />
          </FormControl>
        </LocalizationProvider>
      </Grid>
      <Grid item xs={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl fullWidth margin="normal">
            <DateTimePicker
              disabled={isActualDatesEdited}
              className="modifylabel"
              label="Scheduled End Date & Time"
              value={visitLog.end_date !== '' ? visitLog.end_date : null}
              inputFormat={userPreferredDateTimeFormat}
              minDateTime={dayjs(visitLog.start_date)}
              closeOnSelect
              onChange={(newValue: any) => {
                modifyScheduledDates(newValue, 'end_date');
              }}
              onAccept={(newValue: any) => {
                modifyScheduledDates(newValue, 'end_date');
              }}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  error={validationObj.end_date || visitLog.end_date === 'Invalid date'}
                  helperText={validationObj.end_date && 'Required!'}
                />
              )}
            />
          </FormControl>
        </LocalizationProvider>
      </Grid>
      {!isEventEdited && (visitLog.actual_start_date != null || visitLog.actual_end_date != null) && (
        <>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl fullWidth>
                <DateTimePicker
                  disabled={visitLog.actual_start_date == null}
                  className="modifylabel"
                  label="Actual Start Date & Time"
                  value={visitLog.actual_start_date !== '' ? visitLog.actual_start_date : null}
                  inputFormat={userPreferredDateTimeFormat}
                  maxDateTime={dayjs(new Date())}
                  closeOnSelect
                  onChange={(newValue: any) => {
                    modifyActualDates(newValue, 'actual_start_date');
                  }}
                  onAccept={(newValue: any) => {
                    modifyActualDates(newValue, 'actual_start_date');
                  }}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      error={validationObj.actual_start_date || visitLog.actual_start_date === 'Invalid date'}
                      helperText={validationObj.actual_start_date && 'Required!'}
                    />
                  )}
                />
              </FormControl>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl fullWidth>
                <DateTimePicker
                  disabled={visitLog.actual_end_date == null}
                  className={visitLog.actual_end_date != null ? 'modifylabel' : 'clearModifylabel'}
                  label="Actual End Date & Time"
                  value={visitLog.actual_end_date !== '' ? visitLog.actual_end_date : null}
                  inputFormat={userPreferredDateTimeFormat}
                  minDateTime={dayjs(visitLog.actual_start_date)}
                  maxDateTime={dayjs(new Date())}
                  closeOnSelect
                  onChange={(newValue: any) => {
                    modifyActualDates(newValue, 'actual_end_date');
                  }}
                  onAccept={(newValue: any) => {
                    modifyActualDates(newValue, 'actual_end_date');
                  }}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      error={validationObj.actual_end_date || visitLog.actual_end_date === 'Invalid date'}
                      helperText={validationObj.actual_end_date && 'Required!'}
                    />
                  )}
                />
              </FormControl>
            </LocalizationProvider>
          </Grid>
        </>
      )}
      {isEventEdited && (visitLog.actual_start_date != null || visitLog.actual_end_date != null) && (
        <Grid item xs={12} pt={0.5}>
          <Alert severity="warning">
            {
              'Change of Sub Type, Schedule start date & time and schedule End date & time will reset the Check In and Check Out date and time'
            }
          </Alert>
        </Grid>
      )}
      {isEventEdited && visitLog.visit_type === 'Interim Monitoring Visit' && (
        <Grid item xs={12} pt={0.5}>
          <Alert severity="info">{'Interim Monitoring Report will be updated and must be completed'}</Alert>
        </Grid>
      )}
    </Grid>
  );
}
